(() => {
  if (customElements.get('dfc-garment-image')) return;
  const catalogs = new Map();
  const normalize = (value) => String(value || '').toLowerCase().replace(/[â„¢Â®]/g, '').replace(/[^a-z0-9]/g, '');
  class GarmentImage extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.image = this.querySelector('img');
      this.link = this.querySelector('a');
      this.caption = this.querySelector('figcaption');
      this.version = 0;
      this.previews = new Map();
      this.artwork = new Map();
      this.uploadInput = this.querySelector('[data-custom-artwork]');
      if (this.uploadInput) this.editorReady = import(this.dataset.editor).then(() => {
        if (this.isConnected) this.editor = new window.DFCPrintEditor(this);
        return this.editor;
      });
      this.uploadStatus = this.querySelector('[data-upload-status]');
      this.uploadReady = false;
      this.onUpload = () => this.loadUpload();
      this.uploadInput?.addEventListener('change', this.onUpload);
      const url = this.dataset.catalog;
      const embedded = this.querySelector('[data-dfc-catalog]');
      if (embedded && !catalogs.has(url)) catalogs.set(url, Promise.resolve(JSON.parse(embedded.textContent)));
      if (!catalogs.has(url)) catalogs.set(url, fetch(url).then(response => {
        if (!response.ok) throw new Error('Image catalog unavailable');
        return response.json();
      }));
      this.ready = catalogs.get(url).then(catalog => { this.catalog = catalog; this.refresh(); }).catch(error => {
        this.hidden = true;
        console.warn('DFC garment catalog failed to load', error);
      });
      this.onChange = (event) => {
        if (event.target === this.designColorInput()) {
          this.refresh();
          return;
        }
        if (event.target.closest('variant-selects') || event.target.matches('input[name="id"]')) {
          this.editor?.invalidate();
          this.version += 1;
          this.setAttribute('aria-busy', 'true');
          this.classList.add('dfc-loading');
        }
      };
      this.productInfo?.addEventListener('change', this.onChange);
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId === (this.productInfo?.dataset.originalSection || this.productInfo?.dataset.section)) this.showVariant(data.variant);
        });
      }
      this.observer = new MutationObserver(() => this.refresh());
      const picker = this.productInfo?.querySelector('variant-selects');
      if (picker?.parentElement) this.observer.observe(picker.parentElement, { childList: true });
    }
    disconnectedCallback() {
      this.editor?.destroy();
      this.uploadInput?.removeEventListener('change', this.onUpload);
      if (this.uploadUrl) URL.revokeObjectURL(this.uploadUrl);
      this.version += 1;
      this.unsubscribe?.();
      this.observer?.disconnect();
      this.productInfo?.removeEventListener('change', this.onChange);
    }
    refresh() {
      const selected = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
      if (!selected) return;
      try { this.showVariant(JSON.parse(selected.textContent)); } catch { this.hidden = true; }
    }
    loadUpload() {
      this.editor?.hide();
      this.uploadReady = false;
      this.version += 1;
      if (this.uploadUrl) URL.revokeObjectURL(this.uploadUrl);
      this.uploadUrl = null;
      this.previews.clear();
      this.artwork.clear();
      const file = this.uploadInput.files[0];
      if (!file) { this.uploadStatus.textContent = ''; this.refresh(); return; }
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
        this.uploadInput.value = '';
        this.uploadStatus.textContent = 'Choose a PNG, JPG or WebP image up to 10 MB.';
        this.refresh();
        return;
      }
      this.uploadStatus.textContent = 'Preparing your front-print preview…';
      this.uploadUrl = URL.createObjectURL(file);
      this.refresh();
    }
    designColorInput() {
      return Array.from(this.productInfo?.querySelectorAll('select') || []).find(input =>
        /design color/i.test(input.parentElement?.parentElement?.textContent || '') &&
        !input.closest('variant-selects')
      );
    }
    loadImage(url) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Preview image unavailable: ' + url));
        image.src = url;
      });
    }
    async renderDesign(item, version) {
      this.setAttribute('aria-busy', 'true');
      this.classList.add('dfc-loading');
      try {
        if (!item.print) throw new Error('Print placement unavailable');
        const choice = normalize(this.designColorInput()?.value);
        const ink = ['white', 'black', 'orange', 'original'].includes(choice) ? choice : item.print.ink;
        const artwork = this.uploadInput ? this.uploadUrl : (ink === 'orange' ? this.dataset.artOrange : ink === 'white' ? this.dataset.artWhite : this.dataset.artBlack);
        if (!artwork) throw new Error('Required artwork color unavailable');
        const key = item.url + artwork;
        let result = this.previews.get(key);
        if (!result) {
          if (!this.artwork.has(artwork)) {
            this.artwork.set(artwork, this.loadImage(artwork).catch(error => {
              this.artwork.delete(artwork);
              throw error;
            }));
          }
          const [blank, art] = await Promise.all([
            this.loadImage(item.url + (item.url.includes('?') ? '&' : '?') + 'width=1254').catch(() => this.loadImage(item.url)),
            this.artwork.get(artwork)
          ]);
          if (version !== this.version || !this.isConnected) return;
          if (this.uploadInput) {
            const editor = await this.editorReady;
            if (version !== this.version || !this.isConnected) return;
            if (art.naturalWidth * art.naturalHeight > 40000000) throw new Error('Image exceeds 40 megapixels');
            editor.setArtwork(blank, art, item);
            this.image.alt = this.dataset.designTitle + ' — ' + item.label;
            this.caption.textContent = item.label;
            this.removeAttribute('aria-busy'); this.classList.remove('dfc-loading'); this.hidden = false;
            return;
          }
          const canvas = document.createElement('canvas');
          canvas.width = 1254;
          canvas.height = Math.round(1254 * blank.naturalHeight / blank.naturalWidth);
          const context = canvas.getContext('2d');
          context.drawImage(blank, 0, 0, canvas.width, canvas.height);
          const [x, y, width, height] = item.print.box;
          const designScale = Math.min(1, Math.max(0.1, Number(this.dataset.designScale) || 1));
          const scale = Math.min(width * canvas.width / art.naturalWidth, height * canvas.height / art.naturalHeight) * 0.97 * designScale;
          const aw = art.naturalWidth * scale;
          const ah = art.naturalHeight * scale;
          context.drawImage(art, (x + width / 2) * canvas.width - aw / 2, y * canvas.height, aw, ah);
          // Restore the original drawstrings above the print. Paths are calibrated
          // to the photograph for each garment and color.
          const foreground = item.print.foreground;
          if (foreground?.paths?.length) {
            const garment = document.createElement('canvas');
            garment.width = canvas.width;
            garment.height = canvas.height;
            garment.getContext('2d').drawImage(blank, 0, 0, garment.width, garment.height);
            context.save();
            context.strokeStyle = context.createPattern(garment, 'no-repeat');
            context.lineWidth = foreground.width * canvas.width;
            context.lineCap = 'round';
            context.lineJoin = 'round';
            foreground.paths.forEach(points => {
              context.beginPath();
              points.forEach(([px, py], index) => {
                if (index === 0) context.moveTo(px * canvas.width, py * canvas.height);
                else context.lineTo(px * canvas.width, py * canvas.height);
              });
              context.stroke();
            });
            context.restore();
          }
          result = canvas.toDataURL('image/jpeg', 0.92);
          this.previews.set(key, result);
          if (this.previews.size > 6) this.previews.delete(this.previews.keys().next().value);
        }
        if (version !== this.version || !this.isConnected) return;
        this.image.removeAttribute('srcset');
        this.image.src = result;
        this.image.alt = this.dataset.designTitle + ' — ' + item.label;
        this.link.removeAttribute('target');
        this.link.removeAttribute('href');
        this.caption.textContent = item.label;
        if (this.uploadInput) {
          this.uploadReady = true;
          this.uploadStatus.textContent = 'Your front-print preview is ready. Your original image will be attached to your order.';
        }
        this.removeAttribute('aria-busy');
        this.classList.remove('dfc-loading');
        this.hidden = false;
      } catch (error) {
        if (version !== this.version || !this.isConnected) return;
        if (this.uploadInput) {
          this.uploadReady = false;
          this.uploadStatus.textContent = 'We could not prepare this preview. Please try a different image or garment.';
          this.hidden = false;
          this.removeAttribute('aria-busy');
          this.classList.remove('dfc-loading');
          return;
        }
        this.hidden = true;
        this.removeAttribute('aria-busy');
        console.warn('DFC design preview unavailable', error);
      }
    }
    showVariant(variant) {
      if (!this.catalog || !variant) { this.hidden = true; return; }
      const skuParts = String(variant.sku || '').toUpperCase().split('-');
      const style = Object.keys(this.catalog.styles).find(code => skuParts.includes(code)) || this.catalog.names[normalize(variant.option1)];
      const item = this.catalog.styles[style]?.[normalize(variant.option2)];
      const version = ++this.version;
      if (!item) { this.hidden = true; return; }
      if (this.uploadInput) this.uploadReady = false;
      if (this.uploadUrl || (!this.uploadInput && (this.dataset.artBlack || this.dataset.artWhite))) {
        this.renderDesign(item, version);
        return;
      }
      if (this.image.getAttribute('src') === item.url && this.image.complete && this.image.naturalWidth) {
        this.removeAttribute('aria-busy');
        this.classList.remove('dfc-loading');
        this.hidden = false;
        return;
      }
      this.setAttribute('aria-busy', 'true');
      this.classList.add('dfc-loading');
      const preload = new Image();
      let useOriginal = false;
      const srcset = [360, 720, 1254].map(width => `${item.url}${item.url.includes('?') ? '&' : '?'}width=${width} ${width}w`).join(', ');
      const sizes = '(min-width: 750px) 50vw, 100vw';
      preload.onload = () => {
        if (version !== this.version || !this.isConnected) return;
        this.image.sizes = sizes;
        this.image.srcset = useOriginal ? '' : srcset;
        this.image.src = item.url;
        this.image.alt = item.alt;
        this.link.href = item.url;
        this.caption.textContent = item.label;
        this.removeAttribute('aria-busy');
        this.classList.remove('dfc-loading');
        this.hidden = false;
      };
      preload.onerror = () => {
        if (version !== this.version || !this.isConnected) return;
        if (!useOriginal) {
          useOriginal = true;
          preload.srcset = '';
          preload.src = item.url;
          return;
        }
        this.hidden = true;
        console.warn('DFC garment image failed to load', item.url);
      };
      preload.sizes = sizes;
      preload.srcset = srcset;
      preload.src = item.url;
    }
  }
  customElements.define('dfc-garment-image', GarmentImage);
})();







